/**
 * branchLane — the pane's per-branch widgets (PLAN-prest0n-parallel-branches-20260908).
 *
 * The primary's pending pill stays exactly what it is; when the polled task doc
 * carries a `branches` ledger, the SAME pending message's html gains a rail of
 * rows under the pill — `label · state · m:ss` — one per branch, ordered as the
 * primary opened them. One message, one overlay: the exactly-one-indicator
 * invariant (turnIndicator.ts), the reconcilers and the Stop wiring are untouched.
 *
 * Contract (design record §8):
 *  - labels are the PRIMARY's words (content); the state words here are the
 *    code-authored vocabulary: queued · working · done · didn't finish · stopped ·
 *    continuing in the background (D9: state/failure copy only);
 *  - at most BRANCH_LANE_MAX_ROWS rows, then one "+N more" row;
 *  - the steering receipt is a fact of the FOLD, never of the send (PR-3B,
 *    2026-09-16): a row shows "note sent" from the steer's send until the
 *    child stamps `lastSteerFoldedAtMs` at the boundary where it actually
 *    folded it (at most BRANCH_STEER_SENT_MAX_MS), then "note applied" for
 *    BRANCH_STEER_RECEIPT_MS — the receipt no product ships, and one that
 *    could never lie;
 *  - class-styled: the chat sanitizer keeps `class`/`style` and filters inline
 *    styles to its allow-list (no overflow/text-overflow/position inline), so
 *    ellipsis + narrow-pane rules live in PENDING_WIDGET_CSS (styles.ts);
 *  - the composed html keeps the `pendingSpin` marker so shape detection stays
 *    true, never carries `<article`, never duplicates the stop button id.
 */

import { getPendingWidgetHtml } from './widgetHtml';
import { formatElapsedTurnDurationCompact } from '../prest0nSession/progress';

export type BranchLaneState = 'queued' | 'running' | 'done' | 'failed' | 'cancelled' | 'released';

export interface BranchLaneEntry {
  id: string;
  label: string;
  state: BranchLaneState;
  order?: number;
  stage?: string;
  startedAtMs: number;
  updatedAtMs?: number;
  endedAtMs?: number;
  error?: string;
  released?: boolean;
  steerCount?: number;
  lastSteerAtMs?: number;
  /** PR-3B: stamped by the child at the boundary where it folded a steer. */
  steerFoldedCount?: number;
  lastSteerFoldedAtMs?: number;
  /** PR-3B: notes the branch ended before it could fold (ids only). */
  unansweredNoteIds?: string[];
}

export const BRANCH_LANE_MAX_ROWS = 6;
/** How long a row shows "note applied" after the child FOLDED the steer. */
export const BRANCH_STEER_RECEIPT_MS = 6_000;
/** How long a row shows "note sent" while a steer waits for the child's next step. */
export const BRANCH_STEER_SENT_MAX_MS = 60_000;
/** Label cap for a row (the server caps at 48; belt for older docs). */
const ROW_LABEL_MAX_CHARS = 64;

const TERMINAL: ReadonlySet<BranchLaneState> = new Set(['done', 'failed', 'cancelled', 'released']);

export function isBranchLaneTerminal(state: BranchLaneState | string | undefined): boolean {
  return TERMINAL.has(String(state || '') as BranchLaneState);
}

function asState(raw: unknown): BranchLaneState {
  const s = String(raw || '');
  return (['queued', 'running', 'done', 'failed', 'cancelled', 'released'] as const).includes(s as BranchLaneState)
    ? (s as BranchLaneState)
    : 'queued';
}

/** Ledger map (+ optional open order) → ordered, sanitized rows. Tolerates any shape. */
export function orderBranchEntries(branches: unknown, branchOrder?: unknown): BranchLaneEntry[] {
  if (!branches || typeof branches !== 'object') return [];
  const map = branches as Record<string, Record<string, unknown>>;
  const ids = Array.isArray(branchOrder) ? branchOrder.map((v) => String(v)) : [];
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const id of ids) if (map[id] && !seen.has(id)) { ordered.push(id); seen.add(id); }
  for (const id of Object.keys(map)) if (!seen.has(id)) { ordered.push(id); seen.add(id); }
  const out: BranchLaneEntry[] = [];
  for (const id of ordered) {
    const raw = map[id];
    if (!raw || typeof raw !== 'object') continue;
    const startedAtMs = Number(raw.startedAtMs);
    out.push({
      id: String(raw.id || id),
      label: String(raw.label || '').replace(/\s+/g, ' ').trim().slice(0, ROW_LABEL_MAX_CHARS) || 'a branch',
      state: asState(raw.state),
      ...(typeof raw.order === 'number' ? { order: raw.order } : {}),
      ...(typeof raw.stage === 'string' && raw.stage ? { stage: raw.stage } : {}),
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      ...(typeof raw.updatedAtMs === 'number' ? { updatedAtMs: raw.updatedAtMs } : {}),
      ...(typeof raw.endedAtMs === 'number' ? { endedAtMs: raw.endedAtMs } : {}),
      ...(typeof raw.error === 'string' && raw.error ? { error: raw.error } : {}),
      ...(raw.released === true ? { released: true } : {}),
      ...(typeof raw.steerCount === 'number' ? { steerCount: raw.steerCount } : {}),
      ...(typeof raw.lastSteerAtMs === 'number' ? { lastSteerAtMs: raw.lastSteerAtMs } : {}),
      ...(typeof raw.steerFoldedCount === 'number' ? { steerFoldedCount: raw.steerFoldedCount } : {}),
      ...(typeof raw.lastSteerFoldedAtMs === 'number' ? { lastSteerFoldedAtMs: raw.lastSteerFoldedAtMs } : {}),
      ...(Array.isArray(raw.unansweredNoteIds) ? { unansweredNoteIds: raw.unansweredNoteIds.map((v) => String(v)) } : {}),
    });
  }
  return out.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** The code-authored state vocabulary (D9). */
export function branchStateWord(entry: Pick<BranchLaneEntry, 'state'>): string {
  switch (entry.state) {
    case 'running': return 'working';
    case 'done': return 'done';
    case 'failed': return "didn't finish";
    case 'cancelled': return 'stopped';
    case 'released': return 'continuing in the background';
    default: return 'queued';
  }
}

export type BranchSteerReceipt = 'sent' | 'applied' | null;

/**
 * The steering receipt a row shows, if any (PR-3B): 'applied' briefly after the
 * child FOLDED a steer (`lastSteerFoldedAtMs`); 'sent' while a steer is newer
 * than any fold stamp and younger than BRANCH_STEER_SENT_MAX_MS; nothing on a
 * terminal row. The send alone never reads as applied.
 */
export function branchSteerReceipt(
  entry: Pick<BranchLaneEntry, 'lastSteerAtMs' | 'lastSteerFoldedAtMs' | 'state'>,
  nowMs: number,
): BranchSteerReceipt {
  if (isBranchLaneTerminal(entry.state)) return null;
  const folded = typeof entry.lastSteerFoldedAtMs === 'number' ? entry.lastSteerFoldedAtMs : null;
  const sent = typeof entry.lastSteerAtMs === 'number' ? entry.lastSteerAtMs : null;
  if (folded !== null && nowMs - folded >= 0 && nowMs - folded < BRANCH_STEER_RECEIPT_MS) return 'applied';
  if (sent !== null && (folded === null || folded < sent) && nowMs - sent >= 0 && nowMs - sent < BRANCH_STEER_SENT_MAX_MS) return 'sent';
  return null;
}

export function branchRowClock(entry: Pick<BranchLaneEntry, 'startedAtMs' | 'endedAtMs'>, nowMs: number): string {
  const end = typeof entry.endedAtMs === 'number' ? entry.endedAtMs : nowMs;
  return formatElapsedTurnDurationCompact(Math.max(0, end - entry.startedAtMs));
}

/** The merge line for the moment the last branch lands (the pill's label). */
export function branchMergeLine(entries: ReadonlyArray<Pick<BranchLaneEntry, 'state'>>): string | null {
  if (entries.length === 0) return null;
  if (!entries.every((e) => isBranchLaneTerminal(e.state))) return null;
  return `${entries.length} of ${entries.length} back — putting it together`;
}

function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const FONT = "font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;";

function glyphHtml(state: BranchLaneState): string {
  switch (state) {
    case 'running':
      return '<span class="pending-branch-glyph is-spin" style="width:10px;height:10px;border:2px solid rgba(198,167,94,0.2);border-top-color:var(--brand-gold);border-radius:50%;animation:pendingSpin 0.8s linear infinite;flex-shrink:0;"></span>';
    case 'done':
      return '<span class="pending-branch-glyph is-done" style="width:10px;height:10px;flex-shrink:0;color:var(--brand-gold);font-size:11px;line-height:10px;text-align:center;">&#10003;</span>';
    case 'failed':
      return '<span class="pending-branch-glyph is-failed" style="width:10px;height:10px;flex-shrink:0;color:#92400e;font-size:11px;line-height:10px;text-align:center;">&#10007;</span>';
    case 'cancelled':
      return '<span class="pending-branch-glyph is-stopped" style="width:8px;height:8px;margin:1px;flex-shrink:0;background:var(--brand-text-secondary);border-radius:1px;opacity:0.7;"></span>';
    case 'released':
      return '<span class="pending-branch-glyph is-released" style="width:10px;height:10px;flex-shrink:0;color:var(--brand-gold-hover);font-size:11px;line-height:10px;text-align:center;">&#8594;</span>';
    default:
      return '<span class="pending-branch-glyph is-queued" style="width:8px;height:8px;margin:1px;flex-shrink:0;border:2px solid rgba(198,167,94,0.45);border-radius:50%;"></span>';
  }
}

/** One row. Exported for tests. */
export function buildBranchRowHtml(entry: BranchLaneEntry, nowMs: number): string {
  const state = entry.state;
  const failedColor = state === 'failed' ? 'color:#92400e;' : '';
  const muted = state === 'cancelled' ? 'opacity:0.7;' : '';
  const receipt = branchSteerReceipt(entry, nowMs);
  const stateText = receipt === 'applied' ? 'note applied' : receipt === 'sent' ? 'note sent' : branchStateWord(entry);
  const clock = branchRowClock(entry, nowMs);
  return (
    `<div class="pending-branch-row is-${state}" style="display:flex;align-items:center;gap:8px;min-width:0;max-width:100%;${FONT}font-size:12.5px;font-weight:500;color:var(--brand-gold-hover);letter-spacing:0.01em;${failedColor}${muted}">` +
    glyphHtml(state) +
    `<span class="pending-branch-label" style="flex:1;min-width:0;white-space:nowrap;">${escapeHtml(entry.label)}</span>` +
    `<span class="pending-branch-state" style="flex-shrink:0;white-space:nowrap;opacity:0.8;">${escapeHtml(stateText)}</span>` +
    `<span class="pending-branch-clock" style="flex-shrink:0;white-space:nowrap;color:#8A7742;">${clock}</span>` +
    '</div>'
  );
}

/** The rail under the pill — '' when there is nothing to show. */
export function buildBranchRailHtml(
  entries: ReadonlyArray<BranchLaneEntry>,
  nowMs: number,
  opts?: { maxRows?: number },
): string {
  if (!entries.length) return '';
  const maxRows = Math.max(1, opts?.maxRows ?? BRANCH_LANE_MAX_ROWS);
  const visible = entries.slice(0, maxRows);
  const hidden = entries.length - visible.length;
  const rows = visible.map((e) => buildBranchRowHtml(e, nowMs)).join('');
  const more = hidden > 0
    ? `<div class="pending-branch-row is-more" style="display:flex;align-items:center;gap:8px;min-width:0;${FONT}font-size:12px;color:#8A7742;">` +
      `<span class="pending-branch-label" style="flex:1;min-width:0;white-space:nowrap;">+${hidden} more</span></div>`
    : '';
  return `<div class="pending-branch-rail" style="display:flex;flex-direction:column;gap:4px;width:100%;max-width:100%;min-width:0;padding:2px 0 0 6px;">${rows}${more}</div>`;
}

/**
 * The pending widget with its branch rail — the ONE pending message's html.
 * Falls back to the plain pill when there are no branches.
 */
export function getPendingWidgetWithBranchesHtml(
  statusText: string,
  entries: ReadonlyArray<BranchLaneEntry>,
  nowMs: number = Date.now(),
): string {
  const rail = buildBranchRailHtml(entries, nowMs);
  if (!rail) return getPendingWidgetHtml(statusText);
  return (
    `<div class="pending-widget-stack" style="display:flex;flex-direction:column;gap:6px;max-width:100%;min-width:0;">` +
    getPendingWidgetHtml(statusText) +
    rail +
    '</div>'
  );
}
